import {
  Component,
  computed,
  inject,
  linkedSignal,
  OnDestroy,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';
import {
  Contact,
  ContactGroup,
  ContactGroupExtended,
  TypedLinkedSignal,
} from '@app/store/contacts/contacts.types';
import { ContactsStore } from '@app/store/contacts/contacts.store';
import { ExpansionPanelV2Component } from '@app/framework/expansion-panel-v2/expansion-panel-v2.component';
import { PhonePipe } from '@app/pipes/framework/phone.pipe';
import { OptionsListSimpleComponent } from '@app/framework/overlays/options-list-simple/options-list-simple.component';
import { OptionsAction } from '@app/framework/overlays/option-list.constants';
import { INTERACTION_BAR_STATES } from '@app/framework/constants/interaction-bar.constants';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { FloatingInputComponent } from '@app/framework/inputs/floating-input/floating-input.component';
import { CurrentUserService } from '@app/services/current-user.service';
import { NotificationV2Service } from '@app/notification-v2.service';
import { SplashScreenComponent } from '@app/framework/splash-screen/splash-screen.component';
import { PageLoadingComponent } from '@app/framework/page-loading/page-loading.component';
import { LoadingBarComponent } from '@app/framework/inputs/loading-bar/loading-bar.component';
import { MessagesStateService } from '@app/services/messages-state.service';

@Component({
  selector: 'app-contacts-listing',
  imports: [
    SimpleButtonComponent,
    ExpansionPanelV2Component,
    NgClass,
    PhonePipe,
    OptionsListSimpleComponent,
    FloatingInputComponent,
    FormsModule,
    SplashScreenComponent,
    PageLoadingComponent,
    LoadingBarComponent,
  ],
  templateUrl: './contacts-listing.component.html',
  styleUrl: './contacts-listing.component.scss',
})
export class ContactsListingComponent implements OnInit, OnDestroy {
  contactsStore = inject(ContactsStore);
  interactionBarState = inject(InteractionBarStateService);
  userService = inject(CurrentUserService);
  notif = inject(NotificationV2Service);
  messagesStateService = inject(MessagesStateService);

  isDestroyed$ = new Subject<boolean>();

  contactGroups = this.contactsStore.contactGroups;
  searchText = this.contactsStore.searchText;
  loading = this.contactsStore.isLoading;
  accessibleUsers = toSignal(this.userService.accessibleUsers$);

  readonly companyOptions: Signal<OptionsAction[]> = signal([
    { value: 'rename', label: 'Rename' },
    { value: 'delete', label: 'Delete' },
  ]).asReadonly();

  readonly contactOptions: Signal<OptionsAction[]> = computed(() => {
    return [
      { value: 'edit', label: 'Edit' },
      { value: 'invite', label: 'Invite' },
      { value: 'delete', label: 'Delete' },
    ];
  });

  /**
   * Map of groupId to whether the panel is expanded <br/>
   * If there is search text, all panels are expanded because the user wants to see search results <br/>
   * If there is no search text, it keeps the previous state (so the user doesn't get annoyed) <br/>
   * This is a linkedSignal and it is recomputed when either searchText or contactGroups change.
   */
  isExpanded = linkedSignal<TypedLinkedSignal, Record<number, boolean>>({
    source: () => ({
      searchText: this.searchText(),
      contactGroups: this.contactGroups(),
    }),
    computation: (sources, previousValue) => {
      const map = {};
      if (!sources.searchText) {
        sources.contactGroups.forEach((g) => {
          map[g.id] = previousValue?.value?.[g.id] ?? false;
        });
        return map;
      }

      sources.contactGroups.forEach((g) => {
        map[g.id] = !!sources.searchText;
      });
      return map;
    },
  });

  /** map of groupId to whether the name is being edited */
  isGroupNameEdit = linkedSignal<Record<number, boolean>>(() => {
    const map = {};
    this.contactGroups().forEach((g) => {
      map[g.id] = false;
    });
    return map;
  });

  /** Returns a map of groupId to groupName. <br/>
   What a great thing linkedSignal is. <br/>
   It returns the names of the groups, but if any group is being edited, it keeps the old names so the input doesn't reset. */
  groupNames = linkedSignal<ContactGroup[], Record<number, string>>({
    source: this.contactGroups,
    computation: (newGroups, oldNames) => {
      const nameMap = {};
      if (oldNames && Object.values(this.isGroupNameEdit()).some((value) => value)) {
        return oldNames.value;
      } else {
        newGroups.forEach((group) => {
          nameMap[group.id] = group.name;
        });
      }

      return nameMap;
    },
  });

  ngOnInit() {
    this.contactsStore.loadContactGroups();
  }

  openContactsSidebar() {
    this.interactionBarState.openInteractionBar(INTERACTION_BAR_STATES.CONTACT_SIDEBAR);
  }

  openInvitationsSidebar() {
    this.interactionBarState.openInteractionBar(INTERACTION_BAR_STATES.INVITES_SIDEBAR);
  }

  onContactOptionSelected(option: OptionsAction, contact: Contact, contactGroup: ContactGroup) {
    switch (option.value) {
      case 'edit':
        this.interactionBarState.openInteractionBar(INTERACTION_BAR_STATES.CONTACT_SIDEBAR, {
          contact,
          contactGroup,
        });
        break;
      case 'invite':
        this.interactionBarState.openInteractionBar(INTERACTION_BAR_STATES.INVITES_SIDEBAR, {
          contactId: contact.id,
        });
        break;
      case 'delete': {
        this.notif
          .showConfirmDialog('Are you sure you want to delete this contact?')
          .pipe(takeUntil(this.isDestroyed$))
          .subscribe((response) => {
            if (response) {
              this.contactsStore.deleteContact(contact);
            }
          });
        break;
      }
    }
  }

  onCompanyOptionSelected(option: OptionsAction, contactGroup: ContactGroup) {
    switch (option.value) {
      case 'rename':
        this.isGroupNameEdit.update((oldValue) => {
          const newMap = { ...oldValue };
          newMap[contactGroup.id] = true;
          return newMap;
        });
        break;
      case 'delete': {
        this.notif
          .showConfirmDialog(
            `Are you sure you want to delete ${contactGroup?.name ?? 'this company'}? <br/> All contacts in this group will be deleted.`,
          )
          .pipe(takeUntil(this.isDestroyed$))
          .subscribe((response) => {
            if (response) {
              this.contactsStore.deleteContactGroup(contactGroup);
            }
          });
        break;
      }
    }
  }

  saveGroupName(event: MouseEvent | KeyboardEvent, contactGroup: ContactGroupExtended) {
    if (event instanceof MouseEvent) {
      event.stopPropagation();
    }
    this.contactsStore.renameContactGroup(contactGroup.id, this.groupNames()[contactGroup.id]);

    this.isGroupNameEdit.update((oldValue) => {
      const newMap = { ...oldValue };
      newMap[contactGroup.id] = false;
      return newMap;
    });
  }

  onGroupNameChange(newName: string, contactGroup: ContactGroupExtended) {
    this.groupNames.update((oldNames) => {
      const newNames = { ...oldNames };
      newNames[contactGroup.id] = newName;
      return newNames;
    });
  }

  togglePanelExpansion(id: number) {
    this.isExpanded.update((oldValues) => {
      const newValues = { ...oldValues };
      newValues[id] = !newValues[id];
      return newValues;
    });
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  protected sendMessageTo(contact: Contact) {
    const user = this.accessibleUsers()?.find((user) => user.email === contact.email);
    if (!user) {
      this.notif.showError('Cannot send message to this contact. User not found in our platform.');
      return;
    }

    this.messagesStateService.newMessageTo(user);
  }
}
